import React, { useEffect, useRef, useState } from 'react';
import { Cropper, ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { Box, Button, Typography } from '@mui/material';
import type { StagedFile } from '@securspace/securspace-ui-kit';
import { useCropperRemountOnResize } from './useCropperRemountOnResize';
import { useCropConfirm } from './useCropConfirm';
import {
  getCropActionBarSx,
  getCropErrorSx,
  getCropPanelRootSx,
  getCropStageSx,
} from './cropPanelStyles';

export interface CropPanelProps {
  /** Staged file to crop. `file.previewUrl` is used as the crop source. */
  file: StagedFile;
  /** Fired on a successful crop with a new blob URL set as `previewUrl`. */
  onComplete: (updated: StagedFile) => void;
  /** Fired when the user cancels. No blob URL is generated and no state changes. */
  onCancel: () => void;
  /** Optional. Called with a user-facing message if `canvas.toBlob()` returns null. */
  onError?: (message: string) => void;
  /** True when hosted in `ImagePreviewModal`'s full-screen mode: the cropper fills the available viewport height (iOS Photos-style) instead of a fixed small preview height. */
  fullScreen?: boolean;
  /** Locks the crop box to this ratio (width / height). Omit for a freeform crop. */
  aspectRatio?: number;
  /** Image to crop. Defaults to `file.previewUrl`; pass the original photo so a re-crop can recover what an earlier crop trimmed. */
  src?: string;
}

export const CROP_SOURCE_UNAVAILABLE_MESSAGE = "This photo can't be cropped right now. Please try again later.";

/**
 * True when a hosted photo can't be read for cropping. Cropper.js loads a
 * hosted photo cross-origin and, if the host refuses, silently renders
 * nothing, so this probes the same way first (with a cache-busting query,
 * as Cropper.js does). Blob and data URLs are same-origin and always readable.
 */
const useCrossOriginLoadFailed = (imageUrl: string): boolean => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (!/^https?:/i.test(imageUrl)) return undefined;
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onerror = () => setFailed(true);
    probe.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}timestamp=${Date.now()}`;
    return () => {
      probe.onerror = null;
    };
  }, [imageUrl]);

  return failed;
};

/**
 * Self-contained crop interface. Owns the display → region select → confirm
 * lifecycle; the confirm/blob-conversion state machine is delegated to
 * `useCropConfirm` and the resize-remount workaround to
 * `useCropperRemountOnResize`, keeping this component purely presentational.
 *
 * Does not revoke any blob URLs — that is the caller's responsibility.
 */
const CropPanel = ({ file, onComplete, onCancel, onError, fullScreen, aspectRatio, src }: CropPanelProps) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const cropperInstanceKey = useCropperRemountOnResize();
  const { confirming, error, handleConfirm } = useCropConfirm(file, onComplete, onError);
  const cropSrc = src ?? file.previewUrl;
  const sourceUnavailable = useCrossOriginLoadFailed(cropSrc);

  return (
    <Box sx={getCropPanelRootSx(Boolean(fullScreen))}>
      <Box sx={getCropStageSx(Boolean(fullScreen))}>
        {sourceUnavailable ? (
          <Typography role="alert" color="text.secondary" variant="body2" sx={{ p: 3, textAlign: 'center' }}>
            {CROP_SOURCE_UNAVAILABLE_MESSAGE}
          </Typography>
        ) : (
          <Cropper
            key={cropperInstanceKey}
            ref={cropperRef}
            src={cropSrc}
            aspectRatio={aspectRatio}
            guides
            viewMode={1}
            rotatable
            checkOrientation={false}
            autoCropArea={1}
            // Disabled in favor of the debounced full-remount from useCropperRemountOnResize.
            responsive={false}
            style={{ width: '100%', height: '100%' }}
          />
        )}
      </Box>

      {error && (
        <Typography
          role="alert"
          color="error"
          variant="body2"
          sx={getCropErrorSx(Boolean(fullScreen))}
        >
          {error}
        </Typography>
      )}

      <Box sx={getCropActionBarSx(Boolean(fullScreen))}>
        <Button onClick={onCancel} color="inherit" disabled={confirming}>
          Cancel
        </Button>
        <Button
          onClick={() => handleConfirm(cropperRef.current?.cropper)}
          disabled={confirming || sourceUnavailable}
          variant="contained"
        >
          {confirming ? 'Cropping…' : 'Confirm'}
        </Button>
      </Box>
    </Box>
  );
};

export default CropPanel;


